import { Platform } from '@angular/cdk/platform';
import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { FlightItineraryEnum, SearchStep } from '@constants/flight.constant';
import { AirportDDL } from '@models/airport.model';
import { BaseResponse } from '@models/common.model';
import { FareRuleMapKeyData, FareRuleMapKeyReq } from '@models/master-data.model';
import { Flight, OrderDetail, OrderStatus, PaymentOrderStatus } from '@models/order.model';
import { PaymentResponse9Pay, PaymentTypeEnum } from '@models/payment.model';
import { StoreModel } from '@models/store.model';
import { Store } from '@ngrx/store';
import { DropdownListService } from '@services/dropdown-list.service';
import { MasterDataService } from '@services/master-data.service';
import { OrderService } from '@services/order.service';
import { ToastService } from '@services/toast.service';
import { updateFareRules } from '@store/actions/flight.action';
import { allBookingIsAmadeus } from '@utils/order.util';
import { catchError, EMPTY, forkJoin, from, map, of, switchMap } from 'rxjs';

export type PaymentResolver = {
    detail: OrderDetail | null;
    fareRule: Record<string, FareRuleMapKeyData[]>;
    result?: PaymentOrderStatus | null;
    status?: 'success' | 'cancel';
    payStatus?: 'failed';
};

type PaymentStatusOf9Pay = 'success' | 'cancel';

/**
 * Parse result from 9Pay
 *
 * @param {string} result
 * @return {*}  {(PaymentOrderStatus | null)}
 */
function parseResult9Pay(result: string): PaymentOrderStatus | null {
    try {
        const parse = JSON.parse(atob(result)) as PaymentResponse9Pay;
        if ('status' in parse) {
            return parse.status as PaymentOrderStatus;
        }
        return null;
    } catch (error) {
        return null;
    }
}

/**
 * Payment resolver
 *
 * @export
 * @type {ResolveFn<PaymentResolver | null>}
 */
export const paymentResolver: ResolveFn<PaymentResolver | null> = (route) => {
    const id = route.paramMap.get('id');
    const router = inject(Router);
    const isBrowser = inject(Platform).isBrowser;
    const orderService = inject(OrderService);
    const masterDataService = inject(MasterDataService);
    const dropdownService = inject(DropdownListService);
    const toast = inject(ToastService);
    const store: Store<StoreModel> = inject(Store);
    const resultStr = route.queryParamMap.get('result');
    const statusQuery = route.queryParamMap.get('status');
    const payStatusQuery = route.queryParamMap.get('payStatus');
    const disabledRedirectWhenError = route.queryParamMap.get('isRedirect') === 'false';
    const { step } = route.data;
    const isPaymentStep = step === SearchStep.PAYMENT;
    const isOrderResultStep = step === SearchStep.ORDER_RESULT;
    const navigationEntries = performance.getEntriesByType(
        'navigation'
    ) as PerformanceNavigationTiming[];

    if (!id) {
        router.navigate(['/']);
        return null;
    }

    let status: PaymentStatusOf9Pay;
    let result: PaymentOrderStatus | null = null;
    let payStatus: 'failed' | undefined;

    // Check if payment step and payment status = success or cancel
    if (statusQuery && ['success', 'cancel'].includes(statusQuery) && isPaymentStep && isBrowser) {
        status = statusQuery as PaymentStatusOf9Pay;

        if (resultStr) {
            result = parseResult9Pay(resultStr);
        }
    }

    // Check if order result step and payment failed
    if (isOrderResultStep && payStatusQuery === 'failed') {
        payStatus = payStatusQuery;
        if (isBrowser && navigationEntries.at(0)?.type === 'reload') {
            return from(router.navigate(['flight/search/payment', id])).pipe(map(() => null));
        }
    }

    function checkOrderStatus(orderStatus: number) {
        const allowStatus = [OrderStatus.WaitingPayment, OrderStatus.ForceIssue];
        if (allowStatus.includes(orderStatus)) {
            return true;
        }

        const errorMessages: Record<number, string> = {
            [OrderStatus.Paid]: $localize`:@@search-flight.payment.toast.order-paid:Đơn hàng của bạn đã được thanh toán thành công!`,
            [OrderStatus.Canceled]: $localize`:@@search-flight.payment.toast.order-canceled:Đơn hàng đã bị hủy!`,
            [OrderStatus.Expired]: $localize`:@@search-flight.payment.toast.order-expired:Đơn hàng của bạn đã hết hạn thanh toán!`,
        };

        const errorMessage =
            errorMessages[orderStatus] ||
            $localize`:@@search-flight.payment.toast.order-error:Lỗi đơn hàng, đơn hàng đã bị hủy hoặc đang được xử lý. Vui lòng liên hệ với chúng tôi để biết thêm thông tin!`;
        toast.warn($localize`:@@search-flight.payment.toast.error-title:Lỗi`, errorMessage, {
            closeOnNavigation: true,
        });

        const orderResultStatuses = [
            OrderStatus.Paid,
            OrderStatus.PendingProcess,
            OrderStatus.PaidPending,
        ];
        if (orderResultStatuses.includes(orderStatus)) {
            router.navigate(['/flight/search/order-result', id]);
        } else if (!disabledRedirectWhenError) {
            router.navigate(['/']);
        }

        return false;
    }

    return orderService.getOrderDetailById(id).pipe(
        catchError(() => of(new BaseResponse<OrderDetail | null>({ Data: null }))),
        map((res) => res.Data),
        switchMap((detail) => {
            let group = of({} as Record<string, AirportDDL>);
            if (detail) {
                group = dropdownService.getAirportMap();
            }

            return forkJoin([of(detail), group]);
        }),
        switchMap(([detail, group]) => {
            if (!detail) {
                toast.error(
                    $localize`:@@search-flight.payment.toast.error-title:Lỗi`,
                    $localize`:@@search-flight.payment.toast.order-not-found:Đơn hàng không tồn tại!`,
                    {
                        closeOnNavigation: true,
                    }
                );
                return from(router.navigate(['/'])).pipe(map(() => null));
            }
            if (isPaymentStep && !checkOrderStatus(detail.OrderStatusId)) {
                return disabledRedirectWhenError ? EMPTY : of(null);
            }

            const { OrderStatusId, PaymentTypeCode } = detail;
            const isWaitingPayment =
                OrderStatusId === OrderStatus.WaitingPayment &&
                PaymentTypeCode !== PaymentTypeEnum.POSTPAID;
            if (
                isOrderResultStep &&
                (OrderStatusId === OrderStatus.ForceIssue || isWaitingPayment)
            ) {
                return from(router.navigate(['flight/search/payment', id])).pipe(map(() => null));
            }

            if (allBookingIsAmadeus(detail)) {
                return of({ detail, fareRule: {}, status, result, payStatus });
            }

            const orderBookings = detail.OrderBookings ?? [];
            const ItineraryId = detail.ItineraryId;
            const flights: (Flight & { SystemCode: string })[] = orderBookings.flatMap((o) =>
                o.Flights.map((f) => ({ ...f, SystemCode: o.SystemCode }))
            );

            if (ItineraryId === FlightItineraryEnum.ROUND_TRIP && flights.length === 1) {
                flights.push({
                    ...flights[0],
                    Origin: flights[0].Destination,
                    Destination: flights[0].Origin,
                });
            }

            const req: FareRuleMapKeyReq[] = flights.map((f) => {
                const origin = group[f.Origin];
                const destination = group[f.Destination];
                return {
                    Origin: f.Origin,
                    Destination: f.Destination,
                    CountryCodeOrigin: origin.RegionCode,
                    CountryCodeDestination: destination.RegionCode,
                    IsInternational: origin.International || destination.International,
                    CarrierCode: f.CarrierCode,
                    SystemCode: f.SystemCode,
                    FareCode: f.FareType,
                };
            });

            return masterDataService.getListFareRuleMapKey(req).pipe(
                map((res) => {
                    const fareRule = res.reduce(
                        (acc, cur) => {
                            acc[cur.Origin.concat('-', cur.Destination)] = cur.Data;
                            return acc;
                        },
                        {} as Record<string, FareRuleMapKeyData[]>
                    );

                    store.dispatch(updateFareRules({ data: fareRule }));
                    return { detail, fareRule, status, result, payStatus: payStatus };
                })
            );
        })
    );
};
